/**
 * 智能命中提醒
 * - 监听 history[0] 最新期号
 * - 跟用户 localStorage 收藏夹比对
 * - 命中 → 弹 toast 通知 + 写一条 push
 * - 标记 "已提醒" 防止重复(localStorage)
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Trophy, ChevronRight, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useHistoryStore } from '../stores/history.js';

const NOTIFIED_KEY = 'fucai3d_max_notified_periods';  // 已提醒过的期号列表

function getNotified() {
  try {
    const v = JSON.parse(localStorage.getItem(NOTIFIED_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}
function addNotified(period) {
  try {
    const list = getNotified();
    if (!list.includes(period)) {
      list.push(period);
      localStorage.setItem(NOTIFIED_KEY, JSON.stringify(list.slice(-50)));
    }
  } catch {}
}

function parseCodes(codesStr) {
  // "123 456 789" → [{a:1,b:2,c:3}, ...]
  if (!codesStr) return [];
  return codesStr.split(/\s+/).filter(Boolean).map(s => {
    if (s.length !== 3) return null;
    return { a: +s[0], b: +s[1], c: +s[2], code: s };
  }).filter(Boolean);
}

function checkHit(fav, latest) {
  // fav.codes 包含 latest 的 3 位吗?
  const codes = parseCodes(fav.codes);
  const target = `${latest.a}${latest.b}${latest.c}`;
  return codes.find(c => c.code === target) || null;
}

export function SmartNudge() {
  const history = useHistoryStore(s => s.history);
  const [hits, setHits] = useState(null);  // { fav, hit, latest }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!history || history.length === 0) return;
    const latest = history[0];  // 最新一期
    if (!latest?.p || !latest?.a) return;

    // 已提醒过 → 跳过
    const notified = getNotified();
    if (notified.includes(latest.p)) return;

    // 读收藏
    let favs = [];
    try {
      const raw = localStorage.getItem('fucai3d_max_fav');
      const parsed = JSON.parse(raw || '[]');
      favs = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      favs = [];
    }

    // 找命中的收藏
    const matches = [];
    for (const fav of favs) {
      if (!fav) continue;  // 跳过 null/undefined 项
      const hit = checkHit(fav, latest);
      if (hit) matches.push({ fav, hit, latest });
    }
    if (matches.length === 0) return;

    setHits(matches);
    setDismissed(false);
  }, [history[0]?.p]);

  if (!hits || hits.length === 0) return null;
  if (dismissed) return null;

  const latest = hits[0].latest;
  const target = `${latest.a}${latest.b}${latest.c}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-16 md:top-20 left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[480px] z-50"
      >
        <div
          className="rounded-2xl p-4 shadow-2xl border-2"
          style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.95) 0%, rgba(239,68,68,0.95) 100%)',
            borderColor: 'rgba(251,191,36,0.6)',
            boxShadow: '0 20px 60px rgba(251,191,36,0.4)',
          }}
        >
          <div className="flex items-start gap-3">
            <motion.div
              animate={{ rotate: [0, -15, 15, -15, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
              className="text-3xl"
            >
              🎉
            </motion.div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-white flex items-center gap-2">
                <Trophy size={16} />
                命中了!{hits.length} 个收藏底池
              </div>
              <div className="text-[11px] text-white/90 mt-1">
                第 <span className="font-mono font-bold">{latest.p}</span> 期开出 <span className="font-mono font-bold text-base">{target}</span>
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {hits.slice(0, 3).map((h, i) => (
                  <div key={i} className="text-[10px] text-white/90 bg-white/10 rounded px-2 py-1 flex items-center gap-2">
                    <Sparkles size={9} className="text-yellow-200 flex-shrink-0" />
                    <span className="flex-1 truncate">{h.fav.title}</span>
                    <span className="font-mono font-bold text-yellow-100">🎯 {h.hit.code}</span>
                  </div>
                ))}
                {hits.length > 3 && (
                  <div className="text-[10px] text-white/70 text-center">还有 {hits.length - 3} 个命中...</div>
                )}
              </div>
              <div className="mt-2 text-[10px] text-white/80">
                📌 收藏 <span className="font-bold">{target}</span> 后下次命中自动提醒
              </div>
            </div>
            <button
              onClick={() => {
                setDismissed(true);
                addNotified(latest.p);
              }}
              className="text-white/80 hover:text-white p-1 flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => {
                // 收藏当前期号(下期命中会提醒)
                try {
                  const fav = JSON.parse(localStorage.getItem('fucai3d_max_fav') || '[]');
                  // 找大底池当前期号(本期大底池由 useEngine 计算)
                  // 这里用占位 — 实际可以从 useEngine 拉
                  // 简单:让用户去大底池页面收藏
                  window.location.hash = '#/pool';
                  setDismissed(true);
                  addNotified(latest.p);
                  toast.success('去大底池页面收藏当前期号吧!');
                } catch {}
              }}
              className="flex-1 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold flex items-center justify-center gap-1"
            >
              <Bell size={12} /> 收藏 {target}
            </button>
            <button
              onClick={() => {
                setDismissed(true);
                addNotified(latest.p);
              }}
              className="px-3 py-1.5 rounded-lg bg-white text-orange-600 text-xs font-bold"
            >
              知道了
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * useSmartNudge — hook 版本(在某些页面顶部用)
 */
export function useSmartNudge() {
  const history = useHistoryStore(s => s.history);
  useEffect(() => {
    if (!history || history.length === 0) return;
    const latest = history[0];
    if (!latest?.p || !latest?.a) return;
    const notified = getNotified();
    if (notified.includes(latest.p)) return;

    let favs = [];
    try {
      const raw = localStorage.getItem('fucai3d_max_fav');
      const parsed = JSON.parse(raw || '[]');
      favs = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      favs = [];
    }

    const matches = [];
    for (const fav of favs) {
      if (!fav) continue;
      const hit = checkHit(fav, latest);
      if (hit) matches.push({ fav, hit, latest });
    }
    if (matches.length === 0) return;

    // 触发 toast 提醒(轻量版,非弹窗)
    toast.custom(
      (t) => (
        <div
          className={clsx(
            'rounded-xl p-3 shadow-2xl max-w-sm',
            t.visible ? 'animate-enter' : 'animate-leave'
          )}
          style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.95), rgba(239,68,68,0.95))',
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <Trophy size={18} />
            <div className="flex-1">
              <div className="text-sm font-bold">🎯 收藏命中!{matches.length} 个底池</div>
              <div className="text-[10px] opacity-90">第 {latest.p} 期 {latest.a}{latest.b}{latest.c}</div>
            </div>
            <button
              onClick={() => { toast.dismiss(t.id); addNotified(latest.p); }}
              className="opacity-70 hover:opacity-100"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ),
      { duration: 6000, position: 'top-center' }
    );
    // 不自动标 notified(让用户主动 dismiss / 弹窗)
  }, [history[0]?.p]);
}
